import type { Element, Node } from 'react';
import React, { Children, useContext, useCallback } from 'react';
import { ResponsiveContext } from 'Components/utils/responsive/ResponsiveContext';
import { Slider } from 'Components/frameworks/slider/Slider';
const themes = {
  default: {
    largeCols: 2,
    desktopCols: 4,
    tabletCols: 3,
    navPosition: '',
  },
  short: {
    largeCols: 2,
    desktopCols: 3,
    tabletCols: 3,
    navPosition: 'center',
  },
};
export type ProductInformationSliderProps = {
  children: Node;
  isLarge?: boolean;
  queryLayer: string;
  scrollBackwardLabel?: string;
  scrollForwardLabel?: string;
  theme?: string;
  toggleLayer?: (arg0: number, arg1: boolean) => void;
};

function _ProductInformationSlider({
  children,
  isLarge,
  theme = 'default',
  queryLayer,
  scrollBackwardLabel,
  scrollForwardLabel,
  toggleLayer,
}: ProductInformationSliderProps) {
  const { isDesktop, isTablet } = useContext(ResponsiveContext);

  const openLayer = useCallback(
    (activeItem: number) => (toggleLayer ? toggleLayer(activeItem, true) : null),
    [toggleLayer],
  );

  if (!Children.count(children)) {
    return null;
  }

  const { largeCols, desktopCols, tabletCols, navPosition } = themes[theme];

  const renderItem = (element: Element<any>, index: number) => {
    let { link = {} } = element.props;

    if (!link.url) {
      link = { ...link, onClick: () => openLayer(index) };
    }

    return React.createElement(
      'div',
      {
        className: 'h-full w-280 md:w-auto',
      },
      React.cloneElement(element, {
        link,
        isLarge,
      }),
    );
  };

  const getCols = () => {
    if (isTablet) {
      if (isLarge) {
        return largeCols;
      }

      if (isDesktop) {
        return desktopCols;
      }

      return tabletCols;
    }

    return 0;
  };

  const clonedChildren = Children.map(children, renderItem);
  return (
    <section>
      <Slider
        cols={getCols()}
        hasControls={isTablet}
        id={queryLayer}
        navPosition={navPosition || (isTablet ? 'top' : 'center')}
        nbItemsToScroll={isTablet ? 2 : 1}
        nextLabel={scrollForwardLabel}
        previousLabel={scrollBackwardLabel}
        scrollAreaClassName="overflow-hidden -mx-10"
        scrollAreaContainerClassName="overflow-auto scroll-x scrollbar-hidden snap-x snap-mandatory"
        scrollItemClassName="overflow-hidden px-10 pb-10 flex-none max-w-full"
      >
        {clonedChildren}
      </Slider>
    </section>
  );
}

export const ProductInformationSlider = React.memo(_ProductInformationSlider, () => true);
